import { Form } from 'src/app/interfaces/form';
import { ProfileGroup } from 'src/app/interfaces/profile-group';
import { ProfileWeight } from 'src/app/interfaces/profile-weight';

export interface ProfileGroupMember extends Form {
  formGroupID: number | null;
  formWeightID: number | null;
}

function compareStrings(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function weightSortPosition(weightID: number | null, weights: ProfileWeight[]): number | null {
  if (weightID === null || weightID === undefined) {
    return null;
  }
  const weight = weights.find(w => w.id === weightID);
  return weight ? weight.sortPosition : null;
}

/**
 * The members of ONE group in weight order (sortPosition, then displayName, then id).
 *
 * ⚠ This is member ordering, NOT the grid ordering rule. R1 in the spec is about
 * ordering the GRID OF GROUPS (sortPosition, then displayName, then id — every group
 * currently ties at 99999, so the tiebreak is doing all the work there). This function
 * orders members WITHIN a group, and it is what decision 5's "next available member in
 * weight order" walks. An earlier docstring labelled it R1; that was wrong and the two
 * must not be conflated, because the grid rule is unimplemented until the component
 * half is built.
 * Members whose weight row is missing sort as if they had no weight: after every
 * member with a known weight. The weight join is on `formWeightID` (the FormWeight
 * row); `formGroupID` is pass-through data from the caller (the group's own payload)
 * and is ignored here. Never throws on missing input: a null member list or missing
 * weight row yields placement, not a crash.
 */
export function weightsForGroup(
  group: ProfileGroup,
  weights: ProfileWeight[]
): ProfileGroupMember[] {
  const members = (group?.forms ?? []) as ProfileGroupMember[];
  return [...members].sort((a, b) => {
    const aPos = weightSortPosition(a?.formWeightID ?? null, weights);
    const bPos = weightSortPosition(b?.formWeightID ?? null, weights);
    if (aPos !== null && bPos !== null && aPos !== bPos) {
      return aPos - bPos;
    }
    if (aPos !== null && bPos === null) {
      return -1;
    }
    if (aPos === null && bPos !== null) {
      return 1;
    }
    const byName = compareStrings(a?.displayName ?? '', b?.displayName ?? '');
    if (byName !== 0) {
      return byName;
    }
    return (a?.id ?? 0) - (b?.id ?? 0);
  });
}

/**
 * R2: whether the group needs a weight selector at all. A single-member group renders
 * one card and no selector; two or more members gate the selector on.
 * Missing/empty member lists return false rather than crashing.
 */
export function shouldShowWeightSelector(group: ProfileGroup): boolean {
  return (group?.forms?.length ?? 0) >= 2;
}

/**
 * 🛑 THE CORE RESOLUTION: group + weight -> exactly one profile.
 *
 * This is what the whole feature reduces to — "once a weight is selected it is just a
 * profile" — and it is what a weight click in the selector must call to find the Form
 * to hand to the existing machinery.
 *
 * Returns the member of `group` carrying `weightID`, or null if the group has no such
 * member. Null is a real answer, not an error: the caller must NOT fall through to
 * setClosestByForm on it (see the header of this feature's spec — that path nulls
 * engraving and size and is wrong under a toggle beneath a card). A null here means
 * "this weight is not offered by this group", which the selector should not have
 * rendered in the first place.
 *
 * ⚠ Upstream refuses a save putting two members of one group on the same weight, so a
 * weight identifies at most one member. This does NOT assume that: if the data ever
 * arrives with a collision, the first in weight order wins deterministically rather
 * than the result depending on array order.
 */
export function resolveMember(
  group: ProfileGroup,
  weightID: number | null,
  weights: ProfileWeight[] = []
): ProfileGroupMember | null {
  if (weightID === null || weightID === undefined) {
    return null;
  }
  const ordered = weightsForGroup(group, weights);
  return ordered.find(m => m?.formWeightID === weightID) ?? null;
}

/**
 * 🛑 THE REACHABILITY PREDICATES (ADO #9883). These live here, not in the component, so the
 * invariant below can be PROVEN against the same code the app runs instead of inferred by
 * reading a component or by walking a browser.
 *
 * THE INVARIANT, in Greyson's words 2026-09-17: "if you were to enumerate every profile that the
 * user can end up with if they click on each of the groups and make a selection they should be
 * able to reach the same number of profiles as they did when they were ungrouped." Grouping
 * changes ORGANISATION, never reachability.
 *
 * As set algebra, with S = the selectable ids (available ∪ reachable):
 *
 *     enumerable(groups, S)  =  { m : ∃g ∈ groups,  m ∈ g.forms  ∧  m.id ∈ S }
 *                            =  S ∩ (every profile that belongs to some group)
 *     flat(S)                =  S                       (the flat grid filtered on reachable)
 *
 * So the invariant holds IF AND ONLY IF both of these are true, and each is a real condition
 * rather than a formality:
 *
 *   (a) available ⊆ reachable — otherwise the grouped grid could offer MORE than the flat one.
 *       A property of the rules service, not of this module. Asserted by the test.
 *   (b) every selectable profile belongs to some group — otherwise the grouped grid offers FEWER.
 *       An ungrouped profile is unreachable in the customizer entirely (design record line 316),
 *       and nothing detects it today. Currently 0 of 24 upstream, so the condition holds by luck
 *       rather than by construction.
 *
 * ⚠ A BROWSER CANNOT MEASURE THIS. Selecting a profile RE-RESOLVES the configuration, so
 * `availableForms` changes as you click; walking the cards measures what one click sequence
 * happened to reach, which is path-dependent, not the set reachable from a given configuration.
 * Measured 2026-09-17: the same build reported different reach counts depending on how many
 * cards a run managed to visit. Prove it here; use the browser for rendering, not for sets.
 */
export function membersInSet(
  group: ProfileGroup,
  ids: number[],
  weights: ProfileWeight[]
): ProfileGroupMember[] {
  const set = new Set(ids ?? []);
  return weightsForGroup(group, weights).filter(m => set.has(m?.id));
}

/** Whether anything in `group` is in `ids` — the group-visibility predicate. */
export function groupHasMemberInSet(group: ProfileGroup, ids: number[]): boolean {
  const set = new Set(ids ?? []);
  return (group?.forms ?? []).some(f => set.has(f?.id));
}

/**
 * R3/R4: the default selected member given the available (valid) form IDs.
 * - Considers only members that are available; with no available members returns null
 *   and the caller hides the group (R4).
 * - Prefers the group's flagged default (DefaultFormID) when it is both a member of
 *   the group and available. A DefaultFormID that is not a member is ignored, falling
 *   through to weight order rather than selecting a form from another group.
 * - Otherwise returns the first available member in weight order (R1).
 * Explicit null formWeightID and missing weight rows sort last without crashing.
 */
export function defaultMemberFor(
  group: ProfileGroup,
  availableForms: number[],
  weights: ProfileWeight[]
): ProfileGroupMember | null {
  const available = new Set(availableForms ?? []);
  const ordered = weightsForGroup(group, weights);
  const availableMembers = ordered.filter(m => available.has(m?.id));
  if (availableMembers.length === 0) {
    return null;
  }
  const flagged = group?.defaultFormID;
  if (flagged !== null && flagged !== undefined) {
    const flaggedMember = availableMembers.find(m => m?.id === flagged);
    if (flaggedMember) {
      return flaggedMember;
    }
  }
  return availableMembers[0];
}
